import { Link } from "react-router";
import { ArrowLeft, FileSpreadsheet, CheckCircle, Search, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function CreateProjectSpecDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <FileSpreadsheet className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Create Project Spec for Mask</h1>
              <p className="text-xl text-gray-600">Step-by-step guide</p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              A{" "}
              <a href="/docs/glossary#spec" className="text-blue-600 hover:underline">
                project spec list
              </a>{" "}
              is a list of specification requirements for a{" "}
              <a href="/docs/glossary#mask" className="text-blue-600 hover:underline">
                mask
              </a>
              . The project spec list is a subset of specs from a{" "}
              <a href="/docs/glossary#specs-excel" className="text-blue-600 hover:underline">
                master spec list
              </a>
              , but values can differ from the master values. This allows you to customize
              measurement specifications for your specific project needs while maintaining
              consistency with organizational standards.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-semibold text-blue-900 mb-2">About Master Spec Lists:</h4>
              <p className="text-blue-800 text-sm">
                <a href="/docs/glossary#specs-excel" className="text-blue-600 hover:underline">
                  Master spec lists
                </a>{" "}
                can only be uploaded or changed by admins. They serve as the foundation for all
                project spec lists and ensure consistency across projects.
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Prerequisites:</h4>
              <ul className="text-blue-800 text-sm">
                <li>
                  • Your role is{" "}
                  <a
                    href="/docs/glossary#project-manager"
                    className="text-blue-600 hover:underline"
                  >
                    project manager
                  </a>{" "}
                  or Admin
                </li>
                <li>
                  • Have a{" "}
                  <a href="/docs/glossary#mask" className="text-blue-600 hover:underline">
                    mask
                  </a>{" "}
                  that is linked to a project
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Step-by-Step Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Navigate to Spec Lists</h3>
                <p className="text-gray-700 mb-2">
                  From the main navigation, go to the Spec Lists page to manage all specification
                  lists.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/spec-lists">Go to Spec Lists</Link>
                </Button>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Create New Spec List</h3>
                <p className="text-gray-700">
                  Click the "Create New Spec List" button to start creating your project
                  specification list.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Configure Basic Settings</h3>
                <p className="text-gray-700 mb-2">
                  Fill in the basic information for your spec list:
                </p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Name:</strong> Enter a descriptive name for your spec list
                  </li>
                  <li>
                    • <strong>Mask:</strong> Select the mask through dropdown or use search
                    functionality
                  </li>
                  <li>
                    • <strong>Master Spec List:</strong> Choose the master spec list from the
                    dropdown
                  </li>
                </ul>
                <p className="text-gray-700 mt-2">Click "Next" to proceed to spec selection.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                4
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Select and Configure Specs</h3>
                <p className="text-gray-700 mb-2">
                  Choose which specs you want to measure and customize their values:
                </p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Add/Remove Specs:</strong> Use the checkbox to the left of each spec
                    to include or exclude it
                  </li>
                  <li>
                    • <strong>Available Specs:</strong> All shown specs come from the selected
                    master spec list
                  </li>
                  <li>
                    • <strong>Default Values:</strong> Values are prefilled from the master spec
                    list but can be modified
                  </li>
                  <li>
                    • <strong>Modified Values:</strong> Values that differ from master spec values
                    appear in blue
                  </li>
                </ul>
                <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Search className="w-4 h-4" />
                    <span>Search for specific specs</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4" />
                    <span>Filter specs by device class</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                5
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Create Spec List</h3>
                <p className="text-gray-700">
                  Review your selections and customizations, then click "Create" to save your
                  project spec list. It will now be available for creating test labels.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">After creating your project spec list, you can:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button asChild variant="outline" size="sm">
                <Link to="/docs/create-test-label">Create Test Label</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/spec-lists">View All Spec Lists</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button asChild variant="outline">
            <Link to="/docs">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>
        </div>
      </Main>
    </>
  );
}
